<template>
  <div
    ref="$el"
    class="blog-post"
    :class="type"
    @click="
      push(
        localePath({
          name: 'blog-slug',
          params: {
            slug: post.slug,
          },
        }),
      )
    "
  >
    <div class="head">
      <UiComponentImage
        v-if="post.coverImage && post.coverImage.url && !isResizing"
        :key="`cover-${post.id}-${imageWidth}x${imageHeight}`"
        :alt="post.title"
        :class="{ wait: waitForLoad }"
        :expected-size="imageWidth"
        :extra-params="{
          h: imageHeight,
          w: imageWidth,
          fit: 'crop',
        }"
        :height="imageHeight"
        :lazyload="lazyload"
        :orientation="imageWidth > imageHeight ? 'landscape' : 'portrait'"
        :source="post.coverImage.url"
        :width="imageWidth"
      />
    </div>
    <div class="body">
      <AtomButton
        type="text"
        :to="
          localePath({
            name: 'blog-category-slug',
            params: {
              slug: post.tag.link,
            },
          })
        "
      >
        <UiComponentTag size="s">
          {{ post.tag.name }}
        </UiComponentTag>
      </AtomButton>
      <NuxtLink
        :to="
          localePath({
            name: 'blog-slug',
            params: {
              slug: post.slug,
            },
          })
        "
      >
        <UiComponentHeading
          :as="as"
          :level="level"
        >
          {{ post.title }}
        </UiComponentHeading>
      </NuxtLink>
      <UiComponentParagraph
        v-if="type !== 'featured'"
        class="description"
      >
        {{
          post.description
        }}
      </UiComponentParagraph>
      <UiComponentParagraph
        v-if="type !== 'featured'"
        class="metadata"
        size="s"
      >
        {{ date }}
      </UiComponentParagraph>
      <AtomButton
        v-if="type === 'primary'"
        :to="
          localePath({
            name: 'blog-slug',
            params: {
              slug: post.slug,
            },
          })
        "
        type="text"
        class="read-more"
      >
        <slot /><UiComponentIcon type="arrow-right" />
      </AtomButton>
    </div>
  </div>
</template>

<script setup>
// Composables
const { locale } = useI18n()
const localePath = useLocalePath()
const { push } = useRouter()

// Props
const props = defineProps({
  as: {
    default() {
      return 'heading'
    },
    required: false,
    type: String,
    validator(value) {
      return ['heading', 'paragraph'].indexOf(value) !== -1
    },
  },
  lazyload: {
    default() {
      return true
    },
    required: false,
    type: Boolean,
  },
  level: {
    default() {
      return 3
    },
    required: false,
    type: Number,
    validator(value) {
      return [2, 3].indexOf(value) !== -1
    },
  },
  minutesRead: {
    default() {
      return 'min. read'
    },
    required: true,
    type: String,
  },
  post: {
    required: true,
    type: Object,
  },
  type: {
    default() {
      return 'default'
    },
    required: false,
    type: String,
    validator: (value) => {
      return [
        'default',
        'editor',
        'featured',
        'featured-secondary',
        'horizontal',
        'primary',
      ].indexOf(value) !== -1
    },
  },
  waitForLoad: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
})

// Refs
const $el = ref(null)
const isReady = ref(false)
const isResizing = ref(false)
const offsetHeight = ref(0)
const offsetWidth = ref(0)
const resizeObserver = ref(null)

// Computed Properties
const date = computed(() => {
  if (props.post.updatedAt) {
    return formatDate(props.post.updatedAt)
  }
  else if (props.post.date) {
    return formatDate(props.post.date)
  }
  else {
    return ''
  }
})

const format = computed(() => {
  switch (locale.value) {
    case 'fr':
      return 'D MMMM YYYY'
    default:
      return 'MMMM D, YYYY'
  }
})

const imageHeight = computed(() => {
  return isReady.value
    ? offsetHeight.value
    : props.type === 'primary'
      ? 248
      : 228
})

const imageWidth = computed(() => {
  return isReady.value ? offsetWidth.value : 328
})

// Watchers
watch(() => props.waitForLoad, (value) => {
  if (!value) {
    onObserve()
  }
})

// Methods
function formatDate(date) {
  const locales = {
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    de: 'de-DE',
  }

  const selectedLocale = locales[locale.value] || locales.en

  return new Intl.DateTimeFormat(selectedLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date))
}

function onObserve() {
  if (isResizing.value) {
    return
  }

  isResizing.value = true

  nextTick(() => {
    const $head = $el.value.querySelector('.head')
    offsetHeight.value = $head.offsetHeight
    offsetWidth.value = $head.offsetWidth
    isResizing.value = false
  })
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  resizeObserver.value?.disconnect()
})

onMounted(() => {
  isReady.value = true

  resizeObserver.value = new ResizeObserver(onObserve)
  resizeObserver.value.observe($el.value)

  if (!props.waitForLoad) {
    onObserve()
  }
})
</script>

<style lang="postcss" scoped>
.blog-post {
  @apply flex cursor-pointer;

  &:hover {
    .head {
      picture {
        @apply transform scale-102;
      }
    }

    .body {
      a {
        @apply text-blizzard-700;
      }
    }
  }

  .head {
    @apply overflow-hidden;

    picture {
      @apply transition-transform duration-500 ease-in-out;

      &.wait {
        @apply opacity-0 !important;
      }
    }
  }

  .body {
    .description {
      @apply line-clamp-3;
    }

    .h1,
    .h2,
    .h3,
    .h4,
    .h5,
    .h6 {
      @apply line-clamp-2;
    }

    a {
      @apply flex transition-colors duration-500 ease-in-out;
    }
  }

  .button {
    @apply px-0 h-auto justify-start;

    .tag {
      @apply w-fit bg-blizzard-100 text-blizzard-700 font-medium;
    }
  }

  &.default {
    @apply flex flex-col w-full;

    .head {
      @apply w-full bg-grey-blue-300 rounded-[24px] overflow-hidden;
      height: 228px;

      @screen xl {
        height: 265px;
      }

      picture {
        @apply w-full rounded-none;

        img {
          @apply object-cover;
        }
      }
    }

    .body {
      @apply mt-6;

      a {
        @apply mt-4;

        .h2,
        .h3 {
          @apply w-full mt-0 font-medium text-origin text-winter-green-900 leading-7;

          @screen mdminus {
            @apply text-xl;
          }

          @screen mdplus {
            @apply text-xl;
          }
        }
      }

      p {
        @apply mt-2 text-grey-blue-600 text-sm leading-6;

        &.metadata {
          @apply mt-2 text-xs text-grey-blue-600;
        }
      }
    }
  }

  &.primary {
    @apply flex flex-col w-full bg-white rounded-4xl shadow-light2 transition-shadow ease-in-out duration-300;

    @screen md {
      @apply flex-row;
    }

    &:hover {
      @apply shadow-light4;
    }

    .head {
      @apply flex rounded-t-[24px] rounded-b-none overflow-hidden;
      height: 248px;

      @screen md {
        @apply items-stretch flex-shrink-0 h-[unset] rounded-none rounded-l-[24px];
        width: 340px;
      }

      @screen xl {
        width: 510px;
      }

      picture {
        @apply rounded-none;
      }
    }

    .body {
      @apply p-6;

      @screen md {
        @apply p-10 pr-6;
      }

      @screen xl {
        @apply py-13 px-10;
      }

      a {
        @apply mt-3;

        .h2,
        .h3 {
          @apply mt-0 font-medium text-origin leading-7;

          @screen mdminus {
            @apply text-xl;
          }

          @screen mdplus {
            @apply text-xl;
          }
        }
      }

      p {
        @apply mt-1 text-grey-blue-600 font-normal;

        &.metadata {
          @apply mt-3 text-sm text-grey-blue-600;
        }
      }

      .read-more {
        @apply mt-7 font-bold;
      }
    }
  }

  &.featured {
    @apply flex-col shadow-light2 rounded-[24px] bg-white overflow-hidden transition-shadow duration-300 ease-out;

    &:hover {
      @apply shadow-light4;
    }

    .head {
      @apply w-full relative overflow-hidden;
      height: 231px;

      @screen xl {
        height: 191px;
      }

      picture {
        @apply absolute inset-0 rounded-none;
      }
    }

    .body {
      @apply p-6;

      a {
        @apply mt-4;

        .h2,
        .h3 {
          @apply mt-0 font-medium text-origin leading-7;

          @screen mdminus {
            @apply text-xl;
          }

          @screen mdplus {
            @apply text-xl;
          }
        }
      }
    }
  }

  &.featured-secondary {
    @apply flex flex-col;

    @screen md {
      @apply flex flex-row items-center;
    }

    .head {
      @apply rounded-4xl flex-shrink-0;
      height: 240px;

      @screen md {
        height: 171px;
        width: 245px;
      }

      @screen xl {
        height: 126px;
        width: 180px;
      }
    }

    .body {
      @apply mt-6;
      border-radius: 0.8rem !important;

      @screen md {
        @apply mt-0 ml-10;
      }

      @screen xl {
        @apply ml-6;
      }

      a {
        @apply mt-4;

        .h2,
        .h3 {
          @apply mt-0 font-medium text-origin leading-7;

          @screen mdminus {
            @apply text-xl;
          }

          @screen mdplus {
            @apply text-xl;
          }
        }
      }
    }

    &.primary {
      @screen xl {
        @apply relative rounded-4xl;
        height: calc(100% - 40px);
        width: 620px;
      }

      .head {
        @screen xl {
          @apply w-full h-full;
        }
      }

      .body {
        @screen xl {
          @apply absolute ml-0 p-6 rounded-[24px] bg-white shadow-light2 border border-grey-blue-200;
          bottom: 24px;
          left: 24px;
          right: 24px;
        }
      }
    }
  }

  &.horizontal {
    @apply flex flex-col rounded-4xl border-solid border-2 border-grey-blue-200 overflow-hidden;

    @screen md {
      @apply flex-row;
    }

    .head {
      @apply w-full flex-shrink-0;
      height: 248px;

      @screen md {
        @apply h-auto !important;
        width: 245px;
      }

      @screen xl {
        width: 180px;
      }

      picture {
        @apply rounded-none;
      }
    }

    .body {
      @apply p-6;

      a {
        @apply mt-3;

        .h2,
        .h3 {
          @apply mt-0 font-medium text-origin leading-7;

          @screen mdminus {
            @apply text-xl;
          }

          @screen mdplus {
            @apply text-xl;
          }
        }
      }

      .description {
        @apply hidden;
      }

      .metadata {
        @apply mt-3 text-grey-blue-600;
      }
    }
  }
}
</style>
